import { SxProps, Theme } from "@mui/material";

// Common style objects for AlertsPage
export const alertPageStyles = {
  // Date column styles
  dateCell: {
    fontFamily: "monospace",
    fontSize: "0.875rem",
  } as SxProps<Theme>,

  // Type badge styles
  typeBadge: (isTradeSignal: boolean) =>
    ({
      px: 1.5,
      py: 0.5,
      borderRadius: 1,
      fontSize: "0.75rem",
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      backgroundColor: isTradeSignal ? "#e3f2fd" : "#fff3e0",
      color: isTradeSignal ? "#1976d2" : "#f57c00",
      border: `1px solid ${isTradeSignal ? "#bbdefb" : "#ffcc02"}`,
    }) as SxProps<Theme>,

  // Description column styles
  descriptionContainer: {
    p: 1,
    lineHeight: 1.4,
  } as SxProps<Theme>,

  descriptionText: {
    fontSize: "0.8rem",
    fontWeight: 500,
    color: "#333",
    whiteSpace: "pre-line",
    mb: 0.75,
  } as SxProps<Theme>,

  metadataContainer: {
    mt: 1,
    p: 1,
    backgroundColor: "#f8f9fa",
    borderRadius: 0.5,
    border: "1px solid #e9ecef",
  } as SxProps<Theme>,

  // Label styles
  label: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#6c757d",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  } as SxProps<Theme>,

  // Action badge styles
  actionBadge: (isCall: boolean) =>
    ({
      px: 1,
      py: 0.25,
      borderRadius: 1,
      fontSize: "0.75rem",
      fontWeight: 600,
      backgroundColor: isCall ? "#e3f2fd" : "#fff3e0",
      color: isCall ? "#1976d2" : "#f57c00",
    }) as SxProps<Theme>,

  // Symbol badge styles
  symbolBadge: {
    fontFamily: "monospace",
    fontSize: "0.875rem",
    fontWeight: 600,
    color: "#495057",
    backgroundColor: "#e9ecef",
    px: 1,
    py: 0.25,
    borderRadius: 1,
  } as SxProps<Theme>,

  // Price badge styles
  priceBadge: {
    fontFamily: "monospace",
    fontSize: "0.875rem",
    fontWeight: 600,
    color: "#28a745",
    backgroundColor: "#d4edda",
    px: 1,
    py: 0.25,
    borderRadius: 1,
  } as SxProps<Theme>,

  // Leg action badge styles
  legActionBadge: (isBuy: boolean, isCall: boolean) =>
    ({
      px: 0.75,
      py: 0.25,
      borderRadius: 1,
      fontSize: "0.75rem",
      fontWeight: 600,
      backgroundColor: isBuy ? "#d4edda" : isCall ? "#e3f2fd" : "#fff3e0",
      color: isBuy ? "#155724" : isCall ? "#1976d2" : "#f57c00",
    }) as SxProps<Theme>,

  // Leg date styles
  legDate: {
    fontFamily: "monospace",
    fontSize: "0.75rem",
    color: "#6c757d",
  } as SxProps<Theme>,

  // Leg separator styles
  legSeparator: {
    color: "#6c757d",
  } as SxProps<Theme>,

  // Leg strike price styles
  legStrikePrice: {
    fontFamily: "monospace",
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#495057",
  } as SxProps<Theme>,

  legLastPrice: {
    fontFamily: "monospace",
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#495057",
  } as SxProps<Theme>,

  // Button base styles
  buttonBase: {
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "none",
    px: 2,
    py: 0.5,
    borderRadius: 1,
    boxShadow: "none",
    "&:hover": {
      boxShadow: 1,
    },
  } as SxProps<Theme>,

  // Check button styles
  checkButton: {
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "none",
    px: 2,
    py: 0.5,
    borderRadius: 1,
    boxShadow: "none",
    "&:hover": {
      boxShadow: 1,
    },
  } as SxProps<Theme>,

  // Mark as seen button styles
  markAsSeenButton: (isSeen: boolean) =>
    ({
      fontSize: "0.75rem",
      fontWeight: 600,
      textTransform: "none",
      px: 2,
      py: 0.5,
      borderRadius: 1,
      boxShadow: "none",
      "&:hover": {
        boxShadow: 1,
      },
      "&:disabled": {
        opacity: 0.6,
        backgroundColor: "#f5f5f5",
        color: "#9e9e9e",
      },
    }) as SxProps<Theme>,

  // Field row styles
  fieldRow: {
    mb: 0.75,
    display: "flex",
    alignItems: "center",
    gap: 0.75,
  } as SxProps<Theme>,

  // Action buttons container
  actionButtonsContainer: {
    p: 1,
    display: "flex",
    gap: 0.75,
    flexWrap: "wrap",
  } as SxProps<Theme>,

  // Compact leg item styles
  legItem: {
    ml: 1.5,
    mb: 0.5,
    p: 0.75,
    backgroundColor: "#ffffff",
    borderRadius: 0.5,
    border: "1px solid #dee2e6",
    display: "flex",
    alignItems: "center",
    gap: 0.75,
  } as SxProps<Theme>,

  // Legs container styles
  legsContainer: {
    fontSize: "0.7rem",
    fontWeight: 600,
    color: "#6c757d",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    mb: 0.75,
  } as SxProps<Theme>,
};
